import { Fragment } from "react";

type Props = {
  value: unknown;
  /** Root nodes start expanded; nested objects/arrays start collapsed past this depth. */
  depth?: number;
  autoExpandDepth?: number;
};

/** Renders a single primitive leaf with type-appropriate styling. */
function Leaf({ value }: { value: unknown }): React.JSX.Element {
  if (value === null) return <span className="json-null">null</span>;
  if (value === undefined)
    return <span className="json-undefined">undefined</span>;
  if (typeof value === "string")
    return <span className="json-string">"{value}"</span>;
  if (typeof value === "number")
    return <span className="json-number">{value}</span>;
  if (typeof value === "boolean")
    return <span className="json-boolean">{String(value)}</span>;
  return <span className="json-other">{String(value)}</span>;
}

/**
 * A hand-rolled collapsible JSON tree, built on native `<details>` so expand/collapse needs no
 * component state of its own. Depth beyond `autoExpandDepth` starts collapsed to keep large SDK
 * payloads legible in a narrow iframe panel.
 */
export function JsonTree({
  value,
  depth = 0,
  autoExpandDepth = 2,
}: Props): React.JSX.Element {
  if (value === null || typeof value !== "object") {
    return <Leaf value={value} />;
  }

  const isArray = Array.isArray(value);
  const entries: [string, unknown][] = isArray
    ? (value as unknown[]).map((v, i) => [String(i), v])
    : Object.entries(value as Record<string, unknown>);

  if (entries.length === 0) {
    return <span className="json-empty">{isArray ? "[]" : "{}"}</span>;
  }

  return (
    <details open={depth < autoExpandDepth} className="json-node">
      <summary>
        {isArray ? "[" : "{"}
        <span className="json-count">
          {entries.length} {entries.length === 1 ? "item" : "items"}
        </span>
        {isArray ? "]" : "}"}
      </summary>
      <div className="json-children">
        {entries.map(([k, v]) => (
          <Fragment key={k}>
            <div className="json-entry">
              <span className="json-key">{isArray ? k : `"${k}"`}:</span>{" "}
              <JsonTree
                value={v}
                depth={depth + 1}
                autoExpandDepth={autoExpandDepth}
              />
            </div>
          </Fragment>
        ))}
      </div>
    </details>
  );
}
